import React, { useState } from 'react';
import { 
  ChevronRight, 
  ChevronDown,
  Folder,
  FileText,
  Settings,
  Database,
  Code,
  Layout,
  Package,
  MessageSquare,
  Cog,
  Server
} from 'lucide-react';

const TreeItem = ({ label, comment, children, level = 0 }) => {
  const [isOpen, setIsOpen] = useState(level < 2);
  const hasChildren = children && children.length > 0;

  const getIcon = () => {
    if (!hasChildren) return <FileText size={18} className="text-gray-500" />;
    switch(true) {
      case label.includes('config'):
        return <Settings size={18} className="text-blue-500" />;
      case label.includes('domain'):
        return <Database size={18} className="text-green-500" />;
      case label.includes('infrastructure'):
        return <Server size={18} className="text-purple-500" />;
      case label.includes('interface'):
        return <Layout size={18} className="text-orange-500" />;
      case label.includes('service'):
        return <Cog size={18} className="text-pink-500" />;
      case label.includes('event'):
        return <MessageSquare size={18} className="text-yellow-500" />;
      case label.includes('external'):
        return <Package size={18} className="text-indigo-500" />;
      default:
        return <Folder size={18} className="text-yellow-500" />;
    }
  };

  return (
    <div className="select-none">
      <div 
        className={`flex items-center hover:bg-gray-50 py-1 px-2 rounded cursor-pointer 
          ${hasChildren ? 'hover:bg-gray-100' : 'hover:bg-gray-50'}`}
        onClick={() => hasChildren && setIsOpen(!isOpen)}
      >
        <div style={{ marginLeft: `${level * 20}px` }} className="flex items-center min-w-0">
          <span className="mr-1 flex-shrink-0">
            {hasChildren ? (
              isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />
            ) : null}
          </span>
          <span className="mr-2 flex-shrink-0">{getIcon()}</span>
          <span className="font-mono text-sm truncate">{label}</span>
          {comment && (
            <span className="ml-4 text-gray-500 text-xs italic truncate">
              # {comment}
            </span>
          )}
        </div>
      </div>
      {isOpen && hasChildren && (
        <div>
          {children.map((item, index) => (
            <TreeItem 
              key={`${item.label}-${index}`}
              label={item.label} 
              comment={item.comment}
              children={item.children} 
              level={level + 1} 
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ProjectStructureViewer = () => {
  // La structure complète sera dans la prochaine partie
 const projectStructure = {
  label: "com.example.project",
  children: [
    {
      label: "Application.java",
      comment: "Point d'entrée de l'application"
    },
    {
      label: "config",
      comment: "Configuration globale",
      children: [
        { label: "SecurityConfig.java", comment: "Configuration de sécurité" },
        { label: "AsyncConfig.java", comment: "Configuration des traitements asynchrones" },
        { label: "WebConfig.java", comment: "Configuration web (cors, filtres, etc.)" },
        { label: "WorkflowConfig.java", comment: "Configuration des workflows" }
      ]
    },
    {
      label: "domain",
      comment: "Couche domaine - Règles métier",
      children: [
        {
          label: "common",
          comment: "Éléments communs du domaine",
          children: [
            {
              label: "vo",
              comment: "Value Objects réutilisables",
              children: [
                { label: "BaseValueObject.java", comment: "Value Objects de base" }
              ]
            },
            {
              label: "exception",
              comment: "Exceptions métier",
              children: [
                { label: "BusinessException.java", comment: "Exception métier générique" },
                { label: "ValidationException.java", comment: "Exception de validation" }
              ]
            },
            {
              label: "workflow",
              comment: "Framework workflow générique",
              children: [
                {
                  label: "model",
                  comment: "Modèles de workflow",
                  children: [
                    { label: "WorkflowState.java", comment: "État d'un workflow" },
                    { label: "WorkflowTransition.java", comment: "Transition entre états" },
                    { label: "WorkflowExecution.java", comment: "Exécution d'un workflow" }
                  ]
                },
                {
                  label: "exception",
                  comment: "Exceptions spécifiques aux workflows",
                  children: [
                    { label: "WorkflowException.java", comment: "Exception workflow générique" },
                    { label: "IllegalStateTransitionException.java", comment: "Exception transition invalide" }
                  ]
                }
              ]
            }
          ]
        },
        {
          label: "businessmodule1",
          comment: "Premier module métier",
          children: [
            {
              label: "entity",
              comment: "Entités du module",
              children: [
                { label: "Entity1.java", comment: "Première entité" },
                { label: "Entity2.java", comment: "Seconde entité" }
              ]
            },
            {
              label: "vo",
              comment: "Value Objects du module",
              children: [
                { label: "Id1.java", comment: "Identifiant métier" },
                { label: "Status1.java", comment: "Statut métier" }
              ]
            },
            {
              label: "event",
              comment: "Événements du module",
              children: [
                { label: "Event1CreatedEvent.java", comment: "Évènement de création" },
                { label: "Event1ProcessedEvent.java", comment: "Évènement de traitement" }
              ]
            },
            {
              label: "service",
              comment: "Services du module",
              children: [
                { label: "BusinessService1.java", comment: "Service métier principal" },
                { label: "ValidationService1.java", comment: "Service de validation" }
              ]
            },
            {
              label: "workflow",
              comment: "Workflows du module",
              children: [
                {
                  label: "definition",
                  comment: "Définition des workflows",
                  children: [
                    { label: "WorkflowDefinition1.java", comment: "Définition workflow spécifique" }
                  ]
                },
                {
                  label: "event",
                  comment: "Événements workflow",
                  children: [
                    { label: "WorkflowStartedEvent.java", comment: "Démarrage workflow" },
                    { label: "WorkflowTransitionedEvent.java", comment: "Transition workflow" }
                  ]
                }
              ]
            }
          ]
        },
        {
          label: "businessmodule2",
          comment: "Second module métier (même structure)"
        }
      ]
    },
    {
      label: "application",
      comment: "Couche application - Orchestration",
      children: [
        {
          label: "common",
          comment: "Éléments communs applicatifs",
          children: [
            {
              label: "dto",
              comment: "DTOs communs",
              children: [
                { label: "CommonDTO.java", comment: "DTO partagé" }
              ]
            },
            {
              label: "mapper",
              comment: "Mappeurs communs",
              children: [
                { label: "CommonMapper.java", comment: "Mappeur partagé" }
              ]
            }
          ]
        },
        {
          label: "businessmodule1",
          comment: "Application module 1",
          children: [
            {
              label: "port",
              comment: "Ports du module",
              children: [
                {
                  label: "in",
                  comment: "Ports entrants",
                  children: [
                    { label: "UseCase1.java", comment: "Premier cas d'utilisation" },
                    { label: "UseCase2.java", comment: "Second cas d'utilisation" },
                    { label: "WorkflowExecutionUseCase.java", comment: "Gestion workflow" }
                  ]
                },
                {
                  label: "out",
                  comment: "Ports sortants",
                  children: [
                    { label: "Repository1.java", comment: "Interface repository" },
                    { label: "Gateway1Port.java", comment: "Interface gateway" },
                    { label: "WorkflowPersistencePort.java", comment: "Persistence workflow" }
                  ]
                }
              ]
            },
            {
              label: "dto",
              comment: "DTOs du module",
              children: [
                { label: "Command1.java", comment: "Commande entrante" },
                { label: "Response1.java", comment: "Réponse" },
                {
                  label: "workflow",
                  comment: "DTOs workflow",
                  children: [
                    { label: "WorkflowCommand.java", comment: "Commande workflow" },
                    { label: "WorkflowStatus.java", comment: "Statut workflow" }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      label: "infrastructure",
      comment: "Couche infrastructure - Technique",
      children: [
        {
          label: "persistence",
          comment: "Persistence des données",
          children: [
            {
              label: "entity",
              comment: "Entités de persistance",
              children: [
                { label: "Entity1JPA.java", comment: "Entité JPA 1" },
                { label: "Entity2JPA.java", comment: "Entité JPA 2" },
                { label: "WorkflowExecutionEntity.java", comment: "Entité workflow" },
                { label: "WorkflowTransitionEntity.java", comment: "Entité transition" }
              ]
            },
            {
              label: "repository",
              comment: "Repositories JPA",
              children: [
                { label: "JpaRepository1.java", comment: "Repository JPA 1" },
                { label: "WorkflowExecutionJpaRepository.java", comment: "Repository workflow" }
              ]
            }
          ]
        },
        {
          label: "external",
          comment: "Intégrations externes",
          children: [
            {
              label: "system1",
              comment: "Premier système externe",
              children: [
                { label: "System1Client.java", comment: "Client REST/SOAP" },
                { label: "System1Message.java", comment: "Modèle messages" },
                { label: "System1Adapter.java", comment: "Adaptateur" }
              ]
            }
          ]
        }
      ]
    },
    {
      label: "interface",
      comment: "Couche interface - API",
      children: [
        {
          label: "api",
          comment: "API REST",
          children: [
            {
              label: "controller",
              comment: "Contrôleurs",
              children: [
                { label: "Controller1.java", comment: "Premier contrôleur" },
                { label: "WorkflowController.java", comment: "Contrôleur workflow" }
              ]
            },
            {
              label: "request",
              comment: "Objets requête",
              children: [
                { label: "Request1.java", comment: "Première requête" },
                { label: "WorkflowRequest.java", comment: "Requête workflow" }
              ]
            },
            {
              label: "response",
              comment: "Objets réponse",
              children: [
                { label: "Response1.java", comment: "Première réponse" },
                { label: "WorkflowResponse.java", comment: "Réponse workflow" }
              ]
            }
          ]
        }
      ]
    }
  ]
};



  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <h1 className="text-2xl font-bold mb-6 text-gray-800">
            Clean Architecture Project Structure
          </h1>
          <div className="border rounded-lg p-4 bg-gray-50 overflow-auto">
            <TreeItem {...projectStructure} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectStructureViewer;